import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Screen } from "@/components/layout/Screen";
import { LoadingState } from "@/components/interactions/LoadingState";
import { EmptyState } from "@/components/interactions/EmptyState";
import { DailyRecordForm, DailyRecordData } from "@/components/batch/DailyRecordForm";
import { edgeFunctions } from "@/services/edgeFunctions";
import { getAuthenticatedClient, getCurrentUserId } from "@/services/client";
import { offlineStorage } from "@/features/offline/offlineStorage";
import { syncQueue } from "@/features/offline/syncQueue";
import { useNetworkMonitor } from "@/features/network";
import { useToast } from "@/hooks/use-toast";
import { useSimpleMode } from "@/contexts/SimpleModeContext";
import { Bird, CheckCircle2, ClipboardList } from "lucide-react";

export const DailyRecordScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { isOnline } = useNetworkMonitor();
  const { isSimpleMode } = useSimpleMode();
  const userId = getCurrentUserId();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successDay, setSuccessDay] = useState<number | null>(null);

  // Fetch active batch
  const { data: batch, isLoading } = useQuery({
    queryKey: ["cbs", "active-batch-for-record", userId],
    queryFn: async () => {
      if (!userId) return null;
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from("batches")
        .select("id, placement_date, planned_cycle_days, batch_status, doc_count, cumulative_mortality, current_bird_count, batch_code")
        .eq("user_id", userId)
        .in("batch_status", ["placed", "active"])
        .order("placement_date", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  const currentDay = batch
    ? Math.max(1, Math.floor((Date.now() - new Date(batch.placement_date).getTime()) / 86400000) + 1)
    : 0;

  const maxBirds = batch
    ? (batch.current_bird_count ?? batch.doc_count) - (batch.cumulative_mortality ?? 0)
    : 0;

  const handleSubmit = async (data: DailyRecordData) => {
    if (!batch) return;
    setIsSubmitting(true);

    const today = new Date().toISOString().split("T")[0];

    const payload = {
      batchId: batch.id,
      date: today,
      feedKg: data.feedKg,
      mortalityCount: data.mortalityCount,
      waterLiters: data.waterLiters || undefined,
      tempMin: data.tempMin ?? undefined,
      tempMax: data.tempMax ?? undefined,
      weightSamples:
        data.weightSamples.length > 0
          ? {
              count: data.weightSamples.length,
              avgGrams: Math.round(
                data.weightSamples.reduce((a, b) => a + b, 0) / data.weightSamples.length
              ),
            }
          : undefined,
      notes: data.notes || undefined,
    };

    if (!isOnline) {
      const offlineId = `dr_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      try {
        await offlineStorage.addDailyRecord({
          id: offlineId,
          data: payload,
          synced: false,
          createdAt: new Date(),
        });

        syncQueue.addOperation('CREATE', 'daily_record', { ...payload, _offlineId: offlineId }, 'urgent');

        toast({
          title: "Saved offline",
          description: "Will sync when connected",
        });
      } catch (err) {
        toast({
          title: t("common.error", { defaultValue: "Error" }),
          description: "Failed to save offline",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }
    } else {
      const { data: result, error } = await edgeFunctions.processDailyRecord(payload);

      if (error) {
        toast({
          title: t("common.error", { defaultValue: "Error" }),
          description: error.message,
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      // Bug 2 mirror-write: insert an `activities` row so fn_increment_bonus_score
      // fires and credit_scores.bonus_score is incremented. Best-effort — failure
      // here does NOT roll back the daily_records insert (already committed by
      // the edge function above).
      try {
        if (userId) {
          const client = getAuthenticatedClient();
          const { error: actErr } = await client.from('activities').insert({
            user_id: userId,
            activity_type: 'feeding',
            activity_date: today,
            notes: `Daily record day ${currentDay} for batch ${batch.batch_code}`,
            points_earned: 5,
            batch_id: batch.id,
          });
          if (actErr) {
            console.warn('Bonus mirror-write to activities failed (non-fatal):', actErr.message);
          }
        }
      } catch (mirrorErr) {
        console.warn('Bonus mirror-write to activities threw (non-fatal):', mirrorErr);
      }
    }

    setIsSubmitting(false);
    setSuccessDay(currentDay);
    if ("vibrate" in navigator) navigator.vibrate([50, 50, 100]);

    setTimeout(() => {
      navigate("/");
    }, 1800);
  };

  if (isLoading) {
    return (
      <Screen showTopBar showBottomNav topBarProps={{ title: t("cbs.dailyRecord.title", { defaultValue: "Daily Record" }), showBack: true, showMapPin: false, showSettings: false }}>
        <LoadingState message={t("common.loading", { defaultValue: "Loading…" })} />
      </Screen>
    );
  }

  if (!batch) {
    return (
      <Screen showTopBar showBottomNav topBarProps={{ title: t("cbs.dailyRecord.title", { defaultValue: "Daily Record" }), showBack: true, showMapPin: false, showSettings: false }}>
        <EmptyState
          icon={Bird}
          title={t("cbs.batch.noBatch", { defaultValue: "No Active Batch" })}
          description={t("cbs.batch.noBatchDesc", { defaultValue: "Start a new batch to begin recording." })}
          action={{ label: t("cbs.batch.new", { defaultValue: "Start New Batch" }), onClick: () => navigate("/batch/new") }}
        />
      </Screen>
    );
  }

  return (
    <Screen
      showTopBar
      showBottomNav
      topBarProps={{
        title: isSimpleMode
          ? `📅 Day ${currentDay}`
          : `Day ${currentDay} Record`,
        showBack: true,
        showMapPin: false,
        showSettings: false,
        rightAction: (
          <button
            onClick={() => navigate('/batch/history')}
            className="p-2 rounded-full hover:bg-white/20"
            aria-label="Batch history"
          >
            <ClipboardList className="w-5 h-5 text-white" />
          </button>
        ),
      }}
    >
      {/* Success overlay */}
      <AnimatePresence>
        {successDay !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background/90 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 20 }}
            >
              <CheckCircle2 className="w-24 h-24 text-success" />
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-4 text-xl font-semibold text-foreground"
            >
              {isSimpleMode ? `✅ Day ${successDay}` : t("cbs.dailyRecord.success", { defaultValue: `Day ${successDay} recorded!`, day: successDay })}
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="p-4 pb-8">
        <DailyRecordForm
          maxBirds={maxBirds}
          batchId={batch.id}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      </div>
    </Screen>
  );
};
